import { Quote } from '../../../mocks/quotes';
import { agencyDetails } from '../../../mocks/quotes';

interface QuotePDFPreviewProps {
  quote: Quote;
  onClose: () => void;
  onSend?: () => void;
}

function formatCurrency(amount: number) {
  return `AED ${amount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const categoryIcons: Record<string, string> = {
  Accommodation: 'ri-hotel-line',
  Flight: 'ri-flight-takeoff-line',
  Cruise: 'ri-ship-line',
  Activity: 'ri-ticket-2-line',
  Transfer: 'ri-car-line',
  Insurance: 'ri-shield-check-line',
  'Special Arrangement': 'ri-gift-line',
  Transport: 'ri-train-line',
  Service: 'ri-customer-service-2-line',
  Dining: 'ri-restaurant-line',
};

export default function QuotePDFPreview({ quote, onClose, onSend }: QuotePDFPreviewProps) {
  const brand = agencyDetails.brandColor;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <i className="ri-file-pdf-2-line text-base" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Quote Preview</p>
              <p className="text-xs text-gray-400">{quote.id} · v{quote.version}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onSend && (
              <button
                onClick={onSend}
                className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-fill text-sm" /> Send Quote
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* PDF Body — scrollable */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          <div className="bg-white max-w-2xl mx-auto rounded-xl overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
            {/* Quote Header */}
            <div style={{ backgroundColor: brand }} className="px-8 py-8 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-bold text-white text-sm">
                      {agencyDetails.logo}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{agencyDetails.name}</p>
                      <p className="text-xs text-white/70">{agencyDetails.website}</p>
                    </div>
                  </div>
                  <h1 className="text-2xl font-bold text-white mb-1">TRAVEL QUOTE</h1>
                  <p className="text-white/70 text-sm">{quote.id}</p>
                </div>
                <div className="text-right">
                  <div className="bg-white/20 rounded-xl px-4 py-3 mb-3">
                    <p className="text-xs text-white/70 mb-1">Total Amount</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(quote.totalAmount)}</p>
                    <p className="text-xs text-white/70">{quote.pax} pax · {quote.destination}</p>
                  </div>
                  <p className="text-xs text-white/70">Valid Until</p>
                  <p className="text-sm font-semibold text-white">
                    {new Date(quote.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-8 py-6 space-y-6">
              {/* Client & Trip Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Prepared For</p>
                  <p className="text-base font-bold text-gray-800">{quote.leadName}</p>
                  <p className="text-sm text-gray-500">{quote.leadEmail}</p>
                  <p className="text-sm text-gray-500">{quote.leadPhone}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Trip Details</p>
                  <div className="space-y-1.5 text-sm text-gray-600">
                    <div className="flex gap-2">
                      <i className="ri-map-pin-line text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{quote.destination}</span>
                    </div>
                    <div className="flex gap-2">
                      <i className="ri-calendar-event-line text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>
                        {new Date(quote.travelDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <i className="ri-group-line text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{quote.pax} {quote.pax === 1 ? 'Traveller' : 'Travellers'}</span>
                    </div>
                    <div className="flex gap-2">
                      <i className="ri-user-line text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>Agent: {quote.agent}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote Title */}
              <div>
                <h2 className="text-base font-bold text-gray-800 mb-1">{quote.title}</h2>
                {quote.notes && (
                  <p className="text-sm text-gray-500 leading-relaxed">{quote.notes}</p>
                )}
              </div>

              {/* Line Items */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Services Included</p>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 text-xs font-semibold text-gray-500 bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                    <div className="col-span-6">Description</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-right">Unit Price</div>
                    <div className="col-span-2 text-right">Total</div>
                  </div>
                  {quote.items.map((item, idx) => (
                    <div
                      key={item.id}
                      className={`grid grid-cols-12 px-4 py-3 text-sm ${idx !== quote.items.length - 1 ? 'border-b border-gray-50' : ''}`}
                    >
                      <div className="col-span-6 flex items-start gap-2">
                        <div className="w-6 h-6 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className={`${categoryIcons[item.category] || 'ri-service-line'} text-amber-600 text-xs`} />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium leading-snug">{item.description}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{item.category}</p>
                        </div>
                      </div>
                      <div className="col-span-2 text-center text-gray-600">
                        {item.qty} <span className="text-gray-400 text-xs">{item.unit}</span>
                      </div>
                      <div className="col-span-2 text-right text-gray-600">{formatCurrency(item.unitPrice)}</div>
                      <div className="col-span-2 text-right font-semibold text-gray-800">{formatCurrency(item.total)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium">{formatCurrency(quote.subtotal)}</span>
                  </div>
                  {quote.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>
                        Discount {quote.discountType === 'percent' ? `(${quote.discountValue}%)` : '(Fixed)'}
                      </span>
                      <span className="font-semibold">-{formatCurrency(quote.discountAmount)}</span>
                    </div>
                  )}
                  {quote.taxAmount > 0 && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>VAT ({quote.taxRate}%)</span>
                      <span>+{formatCurrency(quote.taxAmount)}</span>
                    </div>
                  )}
                  <div
                    className="flex justify-between text-base font-bold text-white rounded-xl px-4 py-3 mt-2"
                    style={{ backgroundColor: brand }}
                  >
                    <span>Total Amount</span>
                    <span>{formatCurrency(quote.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Terms &amp; Conditions</p>
                <p className="text-xs text-gray-500 leading-relaxed">{quote.terms}</p>
              </div>

              {/* Agency Footer */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                <div className="text-xs text-gray-400 space-y-0.5">
                  <p className="font-semibold text-gray-600">{agencyDetails.name}</p>
                  <p>{agencyDetails.address}</p>
                  <p>{agencyDetails.phone} · {agencyDetails.email}</p>
                  <p>{agencyDetails.trn}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Prepared by</p>
                  <p className="text-sm font-semibold text-gray-700">{quote.agent}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(quote.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
